import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { useTheme } from '../../../theme';
const STYLE_ID = 'loci-loci-keyframes';
const PULSE_DURATION_MS = 2400;
const DOT_STAGGER_MS = 120;
const LOCI_KEYFRAMES = `
    @keyframes fs-loci-dot-pulse {
      0%, 100% { transform: translate(0, 0); }
      50% {
        transform: translate(
          var(--loci-dx, 0px),
          var(--loci-dy, 0px)
        );
      }
    }

    .fs-loci-dot {
      animation: fs-loci-dot-pulse ${PULSE_DURATION_MS}ms ease-in-out infinite;
      will-change: transform;
    }
  `;
// React 18+ provides useInsertionEffect for style-sheet injection timed before
// layout/paint. Fall back to useLayoutEffect on older builds (shouldn't happen
// since peerDependencies require React >=18.2, but belt-and-braces for SSR).
const useInsertionEffectFallback = React.useInsertionEffect ??
    React.useLayoutEffect;
const useLociKeyframes = () => {
    useInsertionEffectFallback(() => {
        if (typeof document === 'undefined')
            return;
        if (document.getElementById(STYLE_ID))
            return;
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = LOCI_KEYFRAMES;
        document.head.appendChild(style);
    }, []);
};
const useReducedMotion = () => {
    const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(() => typeof window !== 'undefined'
        ? (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
        : false);
    React.useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        if (!mq)
            return;
        const handler = (e) => setPrefersReducedMotion(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return prefersReducedMotion;
};
// viewBox geometry — center (20, 20), ring radius 14, dot radius 2.5
// at the 4 cardinal positions just outside the ring.
const VIEWBOX_SIZE = 40;
const CENTER = 20;
const RING_RADIUS = 14;
const RING_STROKE = 6;
const DOT_RADIUS = 2.5;
const DOT_OFFSET = 16; // distance from center to dot
const DOT_TRAVEL = 8; // pulse travel distance in viewBox units
const DOTS = [
    { id: 'n', cx: CENTER, cy: CENTER - DOT_OFFSET, dx: 0, dy: DOT_TRAVEL, delay: 0 },
    {
        id: 'e',
        cx: CENTER + DOT_OFFSET,
        cy: CENTER,
        dx: -DOT_TRAVEL,
        dy: 0,
        delay: DOT_STAGGER_MS,
    },
    {
        id: 's',
        cx: CENTER,
        cy: CENTER + DOT_OFFSET,
        dx: 0,
        dy: -DOT_TRAVEL,
        delay: DOT_STAGGER_MS * 2,
    },
    {
        id: 'w',
        cx: CENTER - DOT_OFFSET,
        cy: CENTER,
        dx: DOT_TRAVEL,
        dy: 0,
        delay: DOT_STAGGER_MS * 3,
    },
];
const resolveColor = (color, theme) => {
    if (color === 'inherit')
        return 'currentColor';
    if (color === 'secondary')
        return theme.colors.secondary;
    return theme.colors.primary;
};
const LociOBase = ({ size = 56, color = 'primary', animate = false, glow = false, 'aria-label': ariaLabel, className, style, ...rest }) => {
    useLociKeyframes();
    const theme = useTheme();
    const prefersReducedMotion = useReducedMotion();
    const strokeFill = resolveColor(color, theme);
    const shouldAnimate = animate && !prefersReducedMotion;
    // SVG children honor CSS `transform: translate(<px>)` in CSS pixels, not
    // viewBox units. Scale the travel distance so the pulse looks right at every
    // rendered size (dots overshoot into the ring at small sizes, and the pulse
    // becomes invisible at large sizes, when this is left fixed).
    const scaleFactor = size / VIEWBOX_SIZE;
    // Unique per-instance ID so multiple <LociO glow /> can coexist without
    // colliding SVG filter references.
    const reactId = React.useId();
    const glowFilterId = `fs-loci-glow-${reactId.replace(/:/g, '')}`;
    const dotElements = useMemo(() => DOTS.map((dot) => {
        const dotStyle = shouldAnimate
            ? {
                animationDelay: `${dot.delay}ms`,
                // CSS custom properties for the keyframe target. Values are in
                // CSS pixels, pre-scaled by the rendered size.
                ['--loci-dx']: `${dot.dx * scaleFactor}px`,
                ['--loci-dy']: `${dot.dy * scaleFactor}px`,
                transformBox: 'fill-box',
                transformOrigin: 'center',
            }
            : {};
        return (_jsx("circle", { "data-loci-dot": dot.id, className: shouldAnimate ? 'fs-loci-dot' : undefined, cx: dot.cx, cy: dot.cy, r: DOT_RADIUS, fill: strokeFill, style: dotStyle }, dot.id));
    }), [shouldAnimate, strokeFill, scaleFactor]);
    return (_jsxs("svg", { width: size, height: size, viewBox: `0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`, role: "img", "aria-label": ariaLabel ?? 'Loci logo', className: className, style: style, ...rest, children: [glow && (_jsx("defs", { children: _jsx("filter", { id: glowFilterId, x: "-50%", y: "-50%", width: "200%", height: "200%", "data-testid": "loci-glow-filter", children: _jsx("feGaussianBlur", { stdDeviation: "2" }) }) })), glow && (_jsxs("g", { "data-loci-glow": "true", filter: `url(#${glowFilterId})`, opacity: 0.28, "aria-hidden": "true", children: [_jsx("circle", { cx: CENTER, cy: CENTER, r: RING_RADIUS, fill: "none", stroke: strokeFill, strokeWidth: RING_STROKE }), DOTS.map((dot) => (_jsx("circle", { cx: dot.cx, cy: dot.cy, r: DOT_RADIUS, fill: strokeFill }, `glow-${dot.id}`)))] })), _jsx("circle", { "data-loci-ring": "true", cx: CENTER, cy: CENTER, r: RING_RADIUS, fill: "none", stroke: strokeFill, strokeWidth: RING_STROKE }), dotElements] }));
};
export const LociO = React.memo(LociOBase);
LociO.displayName = 'LociO';
